import React from 'react'
import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/atoms/Card';
import { Input } from '@/components/atoms/Input';
import { Button } from '@/components/atoms/Button';
import UnimoFrame from '@/assets/unimo-frame-standard.svg';
import { LineChart, Line, XAxis, YAxis, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import type { QuizInterfaceProps } from './types'

interface Message {
  type: 'ai' | 'user';
  text?: string;
  component?: () => JSX.Element;
}

const QuizInterface: React.FC<QuizInterfaceProps> = ({ onClose }) => {
  const [currentConversationIndex, setCurrentConversationIndex] = useState(0);
  const [messages, setMessages] = useState<Message[]>([]);
  const [isTyping, setIsTyping] = useState(false);
  const [currentStreamingText, setCurrentStreamingText] = useState('');
  const chatEndRef = useRef<HTMLDivElement>(null);

  const AnalyticsDemo = () => {
    const powerData = [
      { time: '00:00', actual: 45, predicted: 44, temp: 21 },
      { time: '04:00', actual: 42, predicted: 48, temp: 20 },
      { time: '08:00', actual: 60, predicted: 65, temp: 22 },
      { time: '12:00', actual: 78, predicted: 80, temp: 24 },
      { time: '16:00', actual: 82, predicted: 75, temp: 25 },
      { time: '20:00', actual: 70, predicted: 68, temp: 23 }
    ];

    return (
      <div className="space-y-4">
        <div className="text-sm text-gray-600 mb-4">
          Here's a real-time analysis of your power consumption with AI predictions:
        </div>

        <Card className="bg-white">
          <CardHeader>
            <CardTitle className="text-sm">Power Usage Analysis & Prediction</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-48">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={powerData}>
                  <XAxis dataKey="time" />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  <Line type="monotone" dataKey="actual" stroke="#000000" name="Actual Usage (kW)" />
                  <Line type="monotone" dataKey="predicted" stroke="#666666" name="AI Prediction (kW)" strokeDasharray="5 5" />
                  <Line type="monotone" dataKey="temp" stroke="#82ca9d" name="Temperature (°C)" />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  };

  const MonitoringSolutions = () => (
    <div className="space-y-4">
      <div className="mb-4 text-sm text-gray-600">
        Here's how we can monitor your installation:
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Card className="bg-white">
          <CardHeader>
            <div className="h-40 -mt-6 -mx-6 mb-4 bg-gray-100 flex items-center justify-center">
              <i className="fas fa-bolt text-5xl text-gray-600"></i>
            </div>
            <div>
              <CardTitle className="text-lg">Power Monitoring</CardTitle>
              <CardDescription>Smart Energy Analytics</CardDescription>
            </div>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-gray-600">Monitor power generation and usage across temporary installations</p>
            <div className="mt-3">
              <div className="text-xs text-gray-500">Features:</div>
              <ul className="text-xs text-gray-500 list-disc ml-4 mt-1">
                <li>Solar & Generator Integration</li>
                <li>Load Balancing Analytics</li>
                <li>Consumption Forecasting</li>
              </ul>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-white">
          <CardHeader>
            <div className="h-40 -mt-6 -mx-6 mb-4 bg-gray-100 flex items-center justify-center">
              <i className="fas fa-temperature-high text-5xl text-gray-600"></i>
            </div>
            <div>
              <CardTitle className="text-lg">Environmental Monitoring</CardTitle>
              <CardDescription>Smart Sensor Integration</CardDescription>
            </div>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-gray-600">Unified monitoring of temperature, humidity, and air quality</p>
            <div className="mt-3">
              <div className="text-xs text-gray-500">Features:</div>
              <ul className="text-xs text-gray-500 list-disc ml-4 mt-1">
                <li>Multi-protocol Support</li>
                <li>Wireless Sensor Networks</li>
                <li>Real-time Alerts</li>
              </ul>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );

  const conversation = useMemo(() => [
    {
      type: 'ai',
      text: "Hello! I'm unimo, your intelligent IoT assistant. I can help you understand your sensor data, set up monitoring solutions, and get real-time insights. What would you like to know about?"
    },
    {
      type: 'user',
      text: "I need to monitor power usage and environmental conditions at a temporary event site. Can you help?"
    },
    {
      type: 'ai',
      text: "Absolutely! I can help you set up comprehensive monitoring for your event site. Let me analyse the best setup based on your needs."
    },
    {
      type: 'ai',
      component: MonitoringSolutions
    },
    {
      type: 'user',
      text: "That looks good. How does the AI analytics work with these sensors?"
    },
    {
      type: 'ai',
      text: "I process your sensor data in real-time to provide actionable insights. Let me show you an example of our analytics capabilities:"
    },
    {
      type: 'ai',
      component: AnalyticsDemo
    },
    {
      type: 'user',
      text: "Can you help me understand the power consumption patterns?"
    },
    {
      type: 'ai',
      text: "Of course! I analyse your power usage data to identify patterns and predict future consumption. For example, I can show you peak usage times, suggest load balancing strategies, and alert you to any anomalies. Would you like me to analyse a specific aspect of your power consumption?"
    }
  ], []);

  const simulateTyping = useCallback((text: string) => {
    setIsTyping(true);
    let currentIndex = 0;

    // Scroll to typing indicator as soon as it appears
    setTimeout(() => {
      chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, 0);

    const interval = setInterval(() => {
      if (currentIndex <= text.length) {
        setCurrentStreamingText(text.slice(0, currentIndex));
        currentIndex++;
        // Keep scrolling to bottom while typing
        chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
      } else {
        clearInterval(interval);
        setIsTyping(false);
        setCurrentStreamingText('');
        setMessages(prev => [...prev, { type: 'ai', text }]);
      }
    }, 30);
  }, []);

  const advanceConversation = useCallback(() => {
    if (currentConversationIndex < conversation.length) {
      const currentMessage = conversation[currentConversationIndex];
      if (currentMessage.type === 'user') {
        setMessages(prev => [...prev, {
          type: 'user' as const,
          text: currentMessage.text
        }]);
        setCurrentConversationIndex(prev => prev + 1);
      } else if (currentMessage.type === 'ai') {
        if (currentMessage.component) {
          setMessages(prev => [...prev, {
            type: 'ai' as const,
            component: currentMessage.component
          }]);
          setCurrentConversationIndex(prev => prev + 1);
        } else if (currentMessage.text) {
          simulateTyping(currentMessage.text);
          setCurrentConversationIndex(prev => prev + 1);
        }
      }
    }
  }, [currentConversationIndex, simulateTyping, conversation]);

  // Ensure we scroll on any message updates
  useEffect(() => {
    if (chatEndRef.current) {
      chatEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages, currentStreamingText]);

  useEffect(() => {
    if (!isTyping && currentConversationIndex < conversation.length) {
      const timeout = setTimeout(advanceConversation, 1000);
      return () => clearTimeout(timeout);
    }
  }, [isTyping, currentConversationIndex, conversation.length, advanceConversation]);

  return (
    <div className="h-full flex flex-col">
      <Card className="h-full flex flex-col">
        {/* Chat Header */}
        <div className="p-4 border-b bg-white flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <div className="h-14 w-14">
              <img src={UnimoFrame} alt="unimo" className="w-full h-full" />
            </div>
            <div>
              <h2 className="text-lg font-medium">unimo</h2>
              <p className="text-sm text-gray-500">Intelligent IoT Assistant</p>
            </div>
          </div>
          {onClose && (
            <Button variant="outline" size="sm" onClick={onClose}>
              Close
            </Button>
          )}
        </div>

        {/* Chat Messages */}
        <CardContent className="flex-1 overflow-y-auto p-4 space-y-4">
          {messages.map((message, index) => (
            <div
              key={index}
              className={`flex ${message.type === 'user' ? 'justify-end' : 'justify-start'}`}
            >
              <div
                className={`w-full p-3 rounded-lg ${message.type === 'user'
                  ? 'bg-gray-900 text-white'
                  : message.component
                    ? 'bg-transparent'
                    : 'bg-gray-100 text-gray-900'
                  }`}
              >
                {message.component ? <message.component /> : message.text}
              </div>
            </div>
          ))}
          {isTyping && (
            <div className="flex justify-start">
              <div className="w-full p-3 rounded-lg bg-gray-100 text-gray-900">
                {currentStreamingText}
                <span className="animate-pulse">▋</span>
              </div>
            </div>
          )}
          <div ref={chatEndRef} />
        </CardContent>

        {/* Input Area */}
        <div className="p-4 border-t bg-white mt-auto">
          <div className="flex space-x-2">
            <Input
              disabled
              placeholder="Simulating conversation..."
              className="flex-1"
            />
            <Button disabled>Send</Button>
          </div>
        </div>
      </Card>
    </div>
  );
};

export default QuizInterface;
